import { useCallback, useEffect, useMemo, useState } from "react";
import { useScheduleData } from "@/components/Scheduler/scheduleData";
import useTitanApp from "../../../../hooks/useTitanApp";
import {
  getScheduleEvents,
  getResources,
  setShowBookingSlots,
} from "../../../../dispatchers/scheduleEventsDispatchers";
import { getDefaultCompanyServices } from "../../../../dispatchers/pharmacyServiceDispatchers";

export type TimeSlot =
  | { time: string; status: "available" }
  | {
      time: string;
      status: "booked";
      patient: string;
      service: string;
      practitioner: string;
    }
  | {
      time: string;
      status: "blocked";
      title: string;
      color?: string;
      type?: string;
    };

export interface MiniCalendarState {
  // visibility + date state
  show: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
  date: Date;
  setDate: (d: Date) => void;
  prevDay: () => void;
  nextDay: () => void;
  resetToday: () => void;

  // filters
  selectedService: string;
  setSelectedService: (v: string) => void;
  selectedPractitioner: string;
  setSelectedPractitioner: (v: string) => void;

  // data for filters + rendering
  loading: boolean;
  services: Array<{ id: string; name: string }>;
  practitioners: string[];
  slots: TimeSlot[];
  availableCount: number;
  bookedCount: number;
  slotInterval: number;

  jumpToDate: (daysOrMonths: number, unit: "days" | "months") => void;
  selectedPatient?: any;
  selectedPatientId?: any;
  selectedPatientDetails?: any;
}

export function useMiniCalendarControl(): MiniCalendarState {
  // external data/hooks
  const { appState, scheduleEventState, pharmacyServiceState, patientState } =
    useTitanApp();
  const { currentPharmacyId, companyId } = appState || ({} as any);
  const {
    scheduleEvents = [],
    resources = [],
    showBookingSlots,
  } = scheduleEventState || ({} as any);
  const { defaultPharmacyServices = [] } = pharmacyServiceState || ({} as any);
  const {
    selectedPatient,
    selectedPatientId,
    selectedPatientDetails,
    selectedPatientConsultation,
  } = patientState || ({} as any);

  const sched = useScheduleData() as any;
  const {
    serviceTypesInfo = [],
    servicePharmacists = [],
    availableDates = [],
    getAvailableTimesForDate,
    getAggregatedTimesForDate,
    getFilteredBookingsForDate,
    getFilteredBlockOutsForDate,
    onServiceTypeSelected,
    onPharmacistSelected,
  } = sched || {};

  // base state
  const [show, setShow] = useState(false);
  const [date, setDate] = useState(new Date());
  const [selectedService, setSelectedService] = useState("all");
  const [selectedPractitioner, setSelectedPractitioner] = useState("all");

  // local derived state
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [practitioners, setPractitioners] = useState<string[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [resourcesData, setResources] = useState<any[]>([]);
  const [slots, setSlots] = useState<TimeSlot[]>([]);

  // determine slot interval based on selected service duration (default 60 mins)
  const slotInterval = useMemo(() => {
    if (selectedService && selectedService !== "all") {
      const svc = (serviceTypesInfo || []).find(
        (s: any) => String(s?._id) === String(selectedService),
      );
      const base =
        parseInt(String((svc as any)?.serviceDuration ?? 0), 10) || 0;
      if (base > 0) return base;
    }
    return 60;
  }, [selectedService, serviceTypesInfo]);

  const toggle = useCallback(() => setShow((s) => !s), []);
  const open = useCallback(() => setShowBookingSlots(true), []);
  const close = useCallback(() => {
    setShowBookingSlots(false);
    setSelectedService("all");
    setSelectedPractitioner("all");
    setSlots([]);
  }, []);

  const prevDay = useCallback(() => {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    setDate(d);
  }, [date]);

  const nextDay = useCallback(() => {
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    setDate(d);
  }, [date]);

  const resetToday = useCallback(() => setDate(new Date()), []);

  const jumpToDate = useCallback(
    (daysOrMonths: number, unit: "days" | "months") => {
      const today = new Date();
      const targetDate = new Date(today);

      if (unit === "days") {
        targetDate.setDate(today.getDate() + daysOrMonths);
      } else {
        targetDate.setMonth(today.getMonth() + daysOrMonths);
      }

      setDate(targetDate);
    },
    [],
  );

  useEffect(() => {
    if (selectedPatientConsultation?.serviceName && services.length > 0) {
      const matchingService = services.find(
        (s) =>
          s.name.toLowerCase() ===
          selectedPatientConsultation.serviceName.toLowerCase(),
      );
      if (matchingService) {
        setSelectedService(matchingService.id);
      }
    }
  }, [selectedPatientConsultation, services]);

  // Initial fetch (use cached redux data when available), only when opened
  useEffect(() => {
    const fetchAll = async () => {
      if (!currentPharmacyId) return;

      const cachedEvents = scheduleEvents || [];
      const cachedResources = resources || [];
      const cachedServices = defaultPharmacyServices || [];
      const needEvents =
        !Array.isArray(cachedEvents) || cachedEvents.length === 0;
      const needResources =
        !Array.isArray(cachedResources) || cachedResources.length === 0;
      const needServices =
        !Array.isArray(cachedServices) || cachedServices.length === 0;
      const needsNetwork = needEvents || needResources || needServices;

      if (!needEvents) setEvents(cachedEvents);
      if (!needResources) setResources(cachedResources);
      if (!needServices) {
        const mappedCached = (
          Array.isArray(cachedServices) ? cachedServices : []
        )
          .filter(
            (s: any) =>
              String(s?.serviceType) !== "1" && String(s?.serviceType) !== "3",
          )
          .map((s: any) => ({
            id: String(s?._id ?? s?.id ?? ""),
            name: String(s?.name ?? s?.serviceName ?? ""),
          }))
          .filter((s: any) => s.id && s.name);
        setServices(mappedCached);
      }
      if (!needResources) {
        const defaultStaff = (
          Array.isArray(cachedResources) ? cachedResources : []
        )
          .filter((r: any) => String(r?.resourceType) === "1")
          .map((r: any) => String(r?.name))
          .filter(Boolean);
        setPractitioners(defaultStaff);
      }

      if (!needsNetwork) return; // everything is cached

      setLoading(true);
      try {
        const [eventsResp, resourcesResp, servicesResp] = await Promise.all([
          needEvents
            ? getScheduleEvents(currentPharmacyId)
            : Promise.resolve(cachedEvents),
          needResources
            ? getResources(companyId, currentPharmacyId)
            : Promise.resolve(cachedResources),
          needServices
            ? getDefaultCompanyServices(companyId)
            : Promise.resolve(cachedServices),
        ]);

        setEvents(Array.isArray(eventsResp) ? eventsResp : []);
        setResources(Array.isArray(resourcesResp) ? resourcesResp : []);
        const mapped = Array.isArray(servicesResp)
          ? (servicesResp || [])
              .filter(
                (s: any) =>
                  String(s?.serviceType) !== "1" &&
                  String(s?.serviceType) !== "3",
              )
              .map((s: any) => ({
                id: String(s?._id ?? s?.id ?? ""),
                name: String(s?.name ?? s?.serviceName ?? ""),
              }))
              .filter((s: any) => s.id && s.name)
          : [];
        setServices(mapped);
        const defaultStaff = (Array.isArray(resourcesResp) ? resourcesResp : [])
          .filter((r: any) => String(r?.resourceType) === "1")
          .map((r: any) => String(r?.name))
          .filter(Boolean);
        setPractitioners(defaultStaff);
      } finally {
        setLoading(false);
      }
    };
    if (showBookingSlots) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBookingSlots, currentPharmacyId, companyId]);

  // When service changes, fetch staff for that service via scheduleData
  useEffect(() => {
    const run = async () => {
      if (!companyId || !currentPharmacyId) return;
      if (selectedService && selectedService !== "all") {
        setLoading(true);
        try {
          await onServiceTypeSelected?.(selectedService);
        } finally {
          setLoading(false);
        }
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, companyId, currentPharmacyId]);

  // Keep practitioners list in sync with selection + fetched data
  useEffect(() => {
    if (selectedService && selectedService !== "all") {
      const names = (
        Array.isArray(servicePharmacists) ? servicePharmacists : []
      )
        .map((p: any) => String(p?.name || ""))
        .filter(Boolean);
      setPractitioners(names);
    } else {
      const names = (Array.isArray(resourcesData) ? resourcesData : [])
        .filter((r: any) => String(r?.resourceType) === "1")
        .map((r: any) => String(r?.name))
        .filter(Boolean);
      setPractitioners(names);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, servicePharmacists, resourcesData]);

  // When pharmacist is selected for a specific service, load availability and show skeleton
  useEffect(() => {
    const run = async () => {
      if (
        selectedService &&
        selectedService !== "all" &&
        selectedPractitioner &&
        selectedPractitioner !== "all"
      ) {
        const target = (servicePharmacists || []).find(
          (p: any) => String(p?.name || "") === String(selectedPractitioner),
        );
        const pharmId = target?.id ? String(target.id) : undefined;
        if (pharmId) {
          setLoading(true);
          try {
            await onPharmacistSelected?.(selectedService, pharmId);
          } finally {
            setLoading(false);
          }
        }
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, selectedPractitioner, servicePharmacists]);

  // Build 24h one-hour slots
  useEffect(() => {
    const pad = (n: number) => String(n).padStart(2, "0");
    const dateY = date.getFullYear();
    const dateM = pad(date.getMonth() + 1);
    const dateD = pad(date.getDate());
    const dateStr = `${dateY}-${dateM}-${dateD}`;

    // Dynamic interval based on selected service (default 60), 24h window
    const intervalMin = slotInterval;
    const startMinutes = 0; // 00:00
    const endMinutes = 24 * 60; // 24:00 (represents 23:59 end)
    const toTime = (mins: number) =>
      `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;

    const dayBookings = Array.isArray(getFilteredBookingsForDate?.(dateStr))
      ? getFilteredBookingsForDate!(dateStr)
      : [];
    const dayBlockOuts = Array.isArray(getFilteredBlockOutsForDate?.(dateStr))
      ? getFilteredBlockOutsForDate!(dateStr)
      : [];

    const filteredBookings = dayBookings.filter((b: any) => {
      const svcOk =
        selectedService === "all" ||
        String(b?.serviceId || b?.serviceType) === String(selectedService);
      const pracOk =
        selectedPractitioner === "all" ||
        String(b?.pharmacist) === String(selectedPractitioner);
      return svcOk && pracOk;
    });

    const normalizedSelectedPract = String(
      selectedPractitioner || "",
    ).toLowerCase();

    const isBlockOutRelevant = (bo: any) => {
      if (selectedPractitioner && selectedPractitioner !== "all") {
        const list = Array.isArray(bo?.affectedPharmacists)
          ? bo.affectedPharmacists
          : [];
        return list
          .map((p: any) => String(p || "").toLowerCase())
          .includes(normalizedSelectedPract);
      }
      return true;
    };

    const hasBookingOverlap = (slotStart: number, slotEnd: number) => {
      return filteredBookings.find((b: any) => {
        const [bh, bm] = String((b?.time || "00:00").slice(0, 5)).split(":");
        const bStart = parseInt(bh || "0", 10) * 60 + parseInt(bm || "0", 10);
        const bDur = parseInt(String(b?.duration || 0), 10) || 0;
        const bEnd = bStart + bDur;
        return bStart < slotEnd && bEnd > slotStart;
      });
    };

    const hasBlockOutOverlap = (slotStart: number, slotEnd: number) => {
      return (dayBlockOuts || []).find((bo: any) => {
        if (!isBlockOutRelevant(bo)) return false;
        const [sh, sm] = String(bo?.startTime || "00:00").split(":");
        const [eh, em] = String(bo?.endTime || "00:00").split(":");
        const boStart = parseInt(sh || "0", 10) * 60 + parseInt(sm || "0", 10);
        const boEnd = parseInt(eh || "0", 10) * 60 + parseInt(em || "0", 10);
        return boStart < slotEnd && boEnd > slotStart;
      });
    };

    const useAvailPharmacist =
      selectedService !== "all" && selectedPractitioner !== "all";
    const availableTimes: string[] = useAvailPharmacist
      ? getAvailableTimesForDate?.(dateStr) || []
      : [];
    const availSet = new Set(
      (availableTimes || []).map((t) => String(t).slice(0, 5)),
    );

    const aggregatedTimes: string[] =
      selectedService !== "all" &&
      (!selectedPractitioner || selectedPractitioner === "all")
        ? getAggregatedTimesForDate?.(dateStr, intervalMin) || []
        : [];
    const aggSet = new Set(
      (aggregatedTimes || []).map((t) => String(t).slice(0, 5)),
    );

    const result: TimeSlot[] = [];
    for (let m = startMinutes; m < endMinutes; m += intervalMin) {
      const t = toTime(m);
      const slotStart = m;
      const slotEnd = m + intervalMin;

      const booking = hasBookingOverlap(slotStart, slotEnd);
      if (booking) {
        result.push({
          time: t,
          status: "booked",
          patient: String(booking.patientName || booking.patient || ""),
          service: String(booking.serviceName || booking.serviceType || ""),
          practitioner: String(booking.pharmacist || ""),
        } as TimeSlot);
        continue;
      }

      const block = hasBlockOutOverlap(slotStart, slotEnd);
      if (block) {
        result.push({
          time: t,
          status: "blocked",
          title: String(block.title || "Block-out"),
          color: String(block.color || "bg-gray-400"),
          type: String(block.type || "other"),
        } as TimeSlot);
        continue;
      }

      if (selectedService === "all") {
        // show no available slots by default until a service is selected
      } else if (useAvailPharmacist) {
        if (availSet.has(t)) {
          result.push({ time: t, status: "available" } as TimeSlot);
        }
      } else {
        if (aggSet.has(t)) {
          result.push({ time: t, status: "available" } as TimeSlot);
        }
      }
    }

    setSlots(result);
  }, [
    date,
    selectedService,
    selectedPractitioner,
    slotInterval,
    getFilteredBookingsForDate,
    getFilteredBlockOutsForDate,
    getAvailableTimesForDate,
    getAggregatedTimesForDate,
  ]);

  const availableCount = useMemo(
    () => slots.filter((s) => s.status === "available").length,
    [slots],
  );
  const bookedCount = useMemo(
    () => slots.filter((s) => s.status === "booked").length,
    [slots],
  );

  return {
    show,
    toggle,
    open,
    close,
    date,
    setDate,
    prevDay,
    nextDay,
    resetToday,
    selectedService,
    setSelectedService,
    selectedPractitioner,
    setSelectedPractitioner,
    loading,
    services,
    practitioners,
    slots,
    availableCount,
    bookedCount,
    slotInterval,
    jumpToDate,
    selectedPatient,
    selectedPatientId,
    selectedPatientDetails,
  };
}
